/**
 * External Tool Service — SSO Launcher
 * Abre herramientas externas con autenticación SSO.
 *
 * Estrategia:
 *   - Localhost         → Pasa token + googleId por URL params
 *   - QA (.llavero.co)  → Escribe cookies compartidas (domain=.llavero.co)
 *   - PROD (.habi.co)   → Escribe cookies compartidas (domain=.habi.co)
 *
 * Usado exclusivamente por LaunchPage (componente de prueba SSO).
 */

import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { ExternalTool, getCurrentEnvironment, getToolUrl } from '../config/external-tools';

const COOKIE_TOKEN = 'habi_auth_token';
const COOKIE_USER  = 'habi_user';

interface SSOAuthData {
  token: string;
  googleId: string;
}

/**
 * Lee los datos de autenticación guardados en localStorage
 * (guardados por LoginPageLaunch al hacer login)
 */
const getAuthFromStorage = (): SSOAuthData | null => {
  const token    = localStorage.getItem('launch_token');
  const googleId = localStorage.getItem('launch_googleId');
  if (!token || !googleId) return null;
  return { token, googleId };
};

/**
 * Obtiene el dominio de cookie según el ambiente
 */
const getCookieDomain = (): string | undefined => {
  const hostname = window.location.hostname;
  if (hostname.includes('llavero.co')) return '.llavero.co';
  if (hostname.includes('habi.co'))    return '.habi.co';
  return undefined; // localhost — sin domain
};

/**
 * Guarda cookies compartidas para SSO automático en QA/PROD
 */
const saveSSOCookies = (auth: SSOAuthData): void => {
  const domain = getCookieDomain();
  const isLocalhost = !domain;

  const cookieOptions = {
    domain,
    expires: 1, // 1 día (solo para pruebas)
    secure: !isLocalhost,
    sameSite: 'lax' as const,
  };

  Cookies.set(COOKIE_TOKEN, auth.token, cookieOptions);
  Cookies.set(COOKIE_USER, JSON.stringify({ googleId: auth.googleId }), cookieOptions);

  console.info(`🍪 SSO cookies guardadas (domain: ${domain ?? 'localhost'})`);
};

/**
 * Verifica conectividad VPN llamando al health endpoint de la herramienta.
 * Usa un timeout de 4s: si no responde, se asume que no hay VPN.
 */
const checkVpnConnection = async (healthUrl: string): Promise<boolean> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch(healthUrl, { signal: controller.signal });
    clearTimeout(timeoutId);
    return res.ok;
  } catch {
    clearTimeout(timeoutId);
    return false;
  }
};

/**
 * Abre una herramienta externa.
 * - VPN: verifica conectividad antes de redirigir, muestra warning si no hay VPN.
 * - Sin SSO: abre la URL directamente.
 * - Con SSO: elige la estrategia según el ambiente.
 */
export const openExternalTool = async (tool: ExternalTool): Promise<void> => {
  if (tool.vpn && tool.vpnHealthUrl) {
    const isConnected = await checkVpnConnection(tool.vpnHealthUrl);

    if (!isConnected) {
      toast.warning('Conéctate a la VPN para acceder a esta herramienta', {
        autoClose: 5000,
      });
      return;
    }

    window.open(getToolUrl(tool), '_blank', 'noopener,noreferrer');
    return;
  }

  if (!tool.sso) {
    window.open(getToolUrl(tool), '_blank', 'noopener,noreferrer');
    return;
  }

  const auth = getAuthFromStorage();

  if (!auth) {
    console.warn('⚠️ No hay datos de autenticación. Abre la herramienta sin SSO.');
    window.open(getToolUrl(tool), '_blank', 'noopener,noreferrer');
    return;
  }

  const env = getCurrentEnvironment();

  if (env === 'local') {
    // Localhost: pasa datos por URL params
    openWithUrlParams(tool, auth);
  } else {
    // QA / PROD: guarda cookies compartidas y abre /login-sso del destino
    saveSSOCookies(auth);
    const ssoUrl = `${getToolUrl(tool)}${tool.ssoPath}`;
    window.open(ssoUrl, '_blank', 'noopener,noreferrer');
    console.info(`🔐 SSO via cookies compartidas → ${ssoUrl}`);
  }
};

/**
 * Abre la herramienta pasando token y googleId por URL params
 * (solo usado en localhost)
 */
const openWithUrlParams = (tool: ExternalTool, auth: SSOAuthData): void => {
  const baseUrl = getToolUrl(tool);
  const ssoUrl  = `${baseUrl}${tool.ssoPath}`;

  const params = new URLSearchParams({
    _autoLogin: 'true',
    _token:     auth.token,
    _googleId:  auth.googleId,
  });

  const fullUrl = `${ssoUrl}?${params.toString()}`;
  window.open(fullUrl, '_blank', 'noopener,noreferrer');

  console.info(`🔐 SSO via URL params → ${ssoUrl}?_autoLogin=true&_token=...&_googleId=...`);
};

